import {Card, Container, createStyles, Group, Menu, rem, Space, Tabs, Text, Title, UnstyledButton} from "@mantine/core";
import {IconChevronDown} from "@tabler/icons-react";
import {useLocation, useNavigate} from "react-router-dom";
import {PropsWithChildren, useCallback} from "react";
import {InvoiceEntity} from "@ots-frontend/ots-data";
import {InvoiceDetails} from "./InvoiceDetails";
import {useTranslation} from "react-i18next";
import {InvoiceAction, InvoiceActionMenuDropdown} from "../../menu/InvoiceActionMenu/InvoiceActionMenu";
import {ObjectChangeLogView} from "../common/ObjectChangeLogView/ObjectChangeLogView";
import {TabItem} from "../../tabs/Tabs/Tabs";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {useDownloadInvoicePdf} from "../../../hooks/useDownloadInvoicePdf";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {Badge} from "../../badge/Badge/Badge";
import {InvoiceLineItemView} from "./InvoiceLineItemView";

const useStyles = createStyles((theme) => ({
  mainSection: {
    paddingBottom: theme.spacing.sm
  }
}));

export type InvoiceProfileProps = PropsWithChildren<{
  invoice: InvoiceEntity
}>

const tabs: TabItem[] = [
  {
    key: "details",
    value: "Details"
  },
  {
    key: "line_items",
    value: "Line Items"
  },
  {
    key: "history",
    value: "Change Logs",
    permissions: ["change_log_view"]
  },
];

export const InvoiceProfile = (props: InvoiceProfileProps) => {
  const {invoice} = props;

  const {classes} = useStyles();

  const {t} = useTranslation()
  const navigate = useNavigate();
  const {pathname, search, state} = useLocation();
  const {baseQueryApi} = useApp();

  const {
    downloadInvoicePdf,
  } = useDownloadInvoicePdf({baseQueryApi})

  const onInvoiceActionClick = useCallback((action: InvoiceAction) => {
    if (action === "DOWNLOAD") {
      downloadInvoicePdf(invoice, undefined)
    }
  }, [invoice, downloadInvoicePdf])

  const items = tabs.map((tab) => (
    <HasAccess accessOnEmpty={true} permissions={tab.permissions} key={tab.key}>
      <Tabs.Tab value={tab.key}>
        {tab.value}
      </Tabs.Tab>
    </HasAccess>
  ));

  return (
    <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
      <Container fluid={true} className={classes.mainSection}>
        <Group position="apart">
          <Title order={4}>Invoice: {invoice.invoiceNumber}</Title>
          <Menu
            width={260}
            position="bottom-end"
            transitionProps={{transition: "pop-top-right"}}
            withinPortal
          >
            <Menu.Target>
              <UnstyledButton>
                <Group spacing={7}>
                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                    {t("common.action.plural", "Actions")}
                  </Text>
                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <InvoiceActionMenuDropdown
                onActionClick={onInvoiceActionClick}
                invoice={invoice}/>
            </Menu.Dropdown>
          </Menu>
        </Group>
        {invoice.disputed &&
          <Badge color="red" variant="filled" my="sm">
            {t("common.status.disputed", "Disputed")}
          </Badge>
        }
      </Container>

      <Card.Section>
        <Container fluid={true}>
          <Tabs
            defaultValue={state?.tabValue ?? "details"}
            onTabChange={(value) => navigate(pathname + search, {replace: true, state: {...state, tabValue: value}})}
          >
            <Tabs.List>{items}</Tabs.List>
            <Space h="md"/>
            <Tabs.Panel value="details">
              <InvoiceDetails invoice={invoice}/>
            </Tabs.Panel>
            <Tabs.Panel value="line_items">
              <InvoiceLineItemView invoice={invoice}/>
            </Tabs.Panel>
            <Tabs.Panel value="history">
              <ObjectChangeLogView entity={invoice}/>
            </Tabs.Panel>
          </Tabs>
        </Container>
      </Card.Section>
    </Card>
  );
};
